import { useEffect, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db, type EntityRequest } from '../services/firebase';

/**
 * Admin-side live reads of the cross-brand `entityRequests` collection (chunk iv).
 *
 * Rules allow an admin to read every request (the brand-owner side is scoped by
 * `hasBrandMembership`). Writes stay callable-only, so these hooks are read-only
 * subscriptions. Status/type/brand filtering and sorting are applied client-side
 * by the page — both queries here use a single index-free shape so no composite
 * Firestore index is required.
 */

/** Open lifecycle states — a request still awaiting an admin decision. */
export const OPEN_STATUSES: EntityRequest['status'][] = ['pending', 'needs_info'];

// Low traffic is expected (the portal caps open requests at 20/brand). 200 is a
// generous v1 ceiling; the page flags when it's hit. No real pagination yet.
const LIST_CAP = 200;

/**
 * Every entity request across all brands, newest-updated first. A single
 * `orderBy('updatedAt')` + `limit` needs no composite index.
 */
export function useAllEntityRequests(): {
  requests: EntityRequest[];
  loading: boolean;
  error: boolean;
  capped: boolean;
} {
  const [requests, setRequests] = useState<EntityRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const q = query(
      collection(db, 'entityRequests'),
      orderBy('updatedAt', 'desc'),
      limit(LIST_CAP),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setRequests(
          snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as EntityRequest),
        );
        setLoading(false);
      },
      () => {
        setError(true);
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  return { requests, loading, error, capped: requests.length >= LIST_CAP };
}

/**
 * Cheap live count of open requests (pending + needs_info) for the nav badge.
 * `where status in [...]` with no `orderBy` is a single-field filter — no index.
 */
export function useOpenEntityRequestsCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const q = query(
      collection(db, 'entityRequests'),
      where('status', 'in', OPEN_STATUSES),
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => setCount(snapshot.size),
      () => setCount(0),
    );
    return unsubscribe;
  }, []);

  return count;
}
